/**
 * App-level hook that handles deep links opened via `appUrlOpen` (universal links
 * into my.enough.app/*, localhost/*, and custom-scheme opens).
 *
 * IMPORTANT: This handler is intentionally restricted to INTERNAL deep links. Content
 * shared into Enough from the OS share sheet is handled by `useShareTargetReceiver` on
 * a dedicated channel (the `ShareReceiver` plugin). Routing external/shared URLs here
 * previously caused internal links (e.g. /grow) to be hijacked into Cart Companion and
 * risked swallowing OAuth/auth returns — so external URLs no longer touch this path.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Capacitor } from '@capacitor/core';
import { App, type URLOpenListenerEvent } from '@capacitor/app';

/** Returns true if the URL points to the app itself (internal deep link). */
function isAppDeepLink(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname.endsWith('enough.app');
  } catch {
    return false;
  }
}

export function useShareReceiver() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handler = (event: URLOpenListenerEvent) => {
      if (!event.url) return;

      // Only handle internal deep links. External/shared URLs are handled on the
      // dedicated share channel (useShareTargetReceiver) and must not be routed here.
      if (isAppDeepLink(event.url)) {
        const { pathname } = new URL(event.url);
        navigate(pathname || '/', { replace: true });
      }
    };

    const listenerPromise = App.addListener('appUrlOpen', handler);

    return () => {
      listenerPromise.then(l => l.remove());
    };
  }, [navigate]);
}
